import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2 } from "lucide-react";
import { Overline } from "./Shared";
import { useReveal } from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ t }) => {
  const c = t.contact;
  const { ref, visible } = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.form.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(c.form.error);
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: MapPin, label: c.address, href: "https://maps.google.com/?q=Riviera+Mbadon" },
    { icon: Phone, label: c.phone, href: "tel:+2250789880088" },
    { icon: Mail, label: c.email, href: "mailto:sales@2aimmob.com" },
  ];

  const inputCls =
    "w-full bg-aa-surface border border-aa-gold/25 focus:border-aa-gold text-aa-cream placeholder:text-aa-cream/40 px-4 py-3.5 font-body text-sm outline-none transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 bg-aa-surface">
      <div ref={ref} className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        {/* Left info */}
        <div className={`reveal ${visible ? "is-visible" : ""}`}>
          <Overline>{c.overline}</Overline>
          <h2 className="font-heading font-light text-aa-cream mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            {c.title}
          </h2>
          <p className="font-body font-light text-aa-cream/65 mt-5 text-base sm:text-lg">{c.subtitle}</p>

          <div className="mt-12 space-y-6">
            <div className="font-heading text-sm uppercase tracking-[0.2em] text-aa-gold">{c.infoTitle}</div>
            {info.map((item, i) => {
              const Icon = item.icon;
              return (
                <a
                  key={i}
                  href={item.href}
                  target={item.icon === MapPin ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`contact-info-${i}`}
                  className="flex items-center gap-4 group"
                >
                  <span className="w-11 h-11 flex items-center justify-center border border-aa-gold/30 text-aa-gold group-hover:bg-aa-gold group-hover:text-aa-green transition-colors duration-400 shrink-0">
                    <Icon size={18} strokeWidth={1.2} />
                  </span>
                  <span className="font-body text-aa-cream/85 group-hover:text-aa-gold transition-colors">
                    {item.label}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Right form */}
        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className={`bg-aa-green border border-aa-gold/20 p-7 sm:p-10 reveal ${visible ? "is-visible" : ""}`}
          style={{ transitionDelay: "0.15s" }}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-name" required value={form.name} onChange={update("name")} placeholder={c.form.name} className={inputCls} />
            <input data-testid="contact-email" required type="email" value={form.email} onChange={update("email")} placeholder={c.form.email} className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder={c.form.phone} className={inputCls} />
            <input data-testid="contact-subject" value={form.subject} onChange={update("subject")} placeholder={c.form.subject} className={inputCls} />
          </div>
          <textarea
            data-testid="contact-message"
            required
            rows={5}
            value={form.message}
            onChange={update("message")}
            placeholder={c.form.message}
            className={`${inputCls} mt-4 resize-none`}
          />
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.2em] bg-aa-gold text-aa-green px-8 py-4 hover:bg-aa-cream transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={1.5} />}
            {loading ? c.form.sending : c.form.submit}
          </button>
        </form>
      </div>
    </section>
  );
};
